import {
  doublePrecision,
  foreignKey,
  jsonb,
  pgEnum,
  pgTable,
  text,
  uniqueIndex,
  unique,
  primaryKey,
  uuid,
  integer,
} from "drizzle-orm/pg-core";
import {
  id,
  sortIndex,
  temporalDate,
  temporalInstant,
  temporalTime,
  temporalTimestamps,
} from "../../utils/sql";
import { user } from "../../auth/core/user.sql";
import { foodServing } from "../food/food.sql";
import { relations } from "drizzle-orm";
import { sql } from "drizzle-orm";
import type { FoodItemMatcher } from "../food-matcher";
import type { Nutrient } from "../nutrient";
import { foodLogSourceGroup } from "../food-log/food-log.sql";

/**
 * Meal-plan persistence schema.
 *
 * Invariants:
 * - Plan children (members, slots, meals, occurrences/items) are keyed by plan IDs.
 * - Lane uniqueness and sort ordering are enforced by composite unique indexes.
 *
 * @see `packages/core/docs/database.md`
 */
export const mealPlanStatus = pgEnum("meal_plan_status", ["draft", "published"]);

export const mealPlan = pgTable("meal_plan", {
  ...id,
  ...temporalTimestamps,
  dateStart: temporalDate("date_start").notNull(),
  dateEnd: temporalDate("date_end").notNull(),
  ownerId: uuid("owner_id")
    .references(() => user.id)
    .notNull(),
  status: mealPlanStatus("status").notNull(),
});

export const mealPlanRelations = relations(mealPlan, ({ many }) => ({
  meals: many(plannedMeal),
  slots: many(plannedMealSlot),
  members: many(mealPlanMember),
  invites: many(mealPlanInvite),
}));

export const mealPlanMember = pgTable(
  "meal_plan_member",
  {
    mealPlanId: uuid("meal_plan_id")
      .references(() => mealPlan.id)
      .notNull(),
    userId: uuid("user_id")
      .references(() => user.id)
      .notNull(),
    timeCreated: temporalInstant("time_created").notNull(),
  },
  (t) => [primaryKey({ name: "meal_plan_member_pk", columns: [t.mealPlanId, t.userId] })],
);

export const mealPlanMemberRelations = relations(mealPlanMember, ({ one }) => ({
  mealPlan: one(mealPlan, {
    fields: [mealPlanMember.mealPlanId],
    references: [mealPlan.id],
  }),
}));

export const mealPlanInvite = pgTable("meal_plan_invite", {
  ...id,
  mealPlanId: uuid("meal_plan_id")
    .references(() => mealPlan.id)
    .notNull(),
  createdByUserId: uuid("created_by_user_id")
    .references(() => user.id)
    .notNull(),
  token: text("token").unique().notNull(),
  maxUses: integer("max_uses").notNull(),
  usedCount: integer("used_count").notNull(),
  timeCreated: temporalInstant("time_created").notNull(),
  timeExpires: temporalInstant("time_expires"),
});

export const mealPlanInviteRelations = relations(mealPlanInvite, ({ one }) => ({
  mealPlan: one(mealPlan, {
    fields: [mealPlanInvite.mealPlanId],
    references: [mealPlan.id],
  }),
}));

export const plannedMealSlot = pgTable(
  "planned_meal_slot",
  {
    ...id,
    ...temporalTimestamps,
    mealPlanId: uuid("meal_plan_id")
      .references(() => mealPlan.id)
      .notNull(),
    name: text("name").notNull(),
    time: temporalTime("time").notNull(),
  },
  (t) => [unique("planned_meal_slot_unique").on(t.mealPlanId, t.id)],
);

export const plannedMealSlotRelations = relations(plannedMealSlot, ({ one, many }) => ({
  mealPlan: one(mealPlan, {
    fields: [plannedMealSlot.mealPlanId],
    references: [mealPlan.id],
  }),
  meals: many(plannedMeal),
  userNutrientTargets: many(plannedMealNutrientTargets),
}));

export const plannedMealNutrientTargets = pgTable(
  "planned_meal_nutrient_targets",
  {
    ...temporalTimestamps,
    mealPlanId: uuid("meal_plan_id").notNull(),
    mealSlotId: uuid("meal_slot_id").notNull(),
    userId: uuid("user_id").notNull(),
    nutrientTargets: jsonb("nutrient_targets").$type<Nutrient.Targets>().notNull(),
  },
  (t) => [
    primaryKey({
      name: "planned_meal_nutrient_targets_pk",
      columns: [t.mealPlanId, t.mealSlotId, t.userId],
    }),
    foreignKey({
      name: "planned_meal_nutrient_targets_member_fk",
      columns: [t.mealPlanId, t.userId],
      foreignColumns: [mealPlanMember.mealPlanId, mealPlanMember.userId],
    }),
    foreignKey({
      name: "planned_meal_nutrient_targets_slot_fk",
      columns: [t.mealPlanId, t.mealSlotId],
      foreignColumns: [plannedMealSlot.mealPlanId, plannedMealSlot.id],
    }),
  ],
);

export const plannedMealNutrientTargetsRelations = relations(
  plannedMealNutrientTargets,
  ({ one }) => ({
    mealPlan: one(mealPlan, {
      fields: [plannedMealNutrientTargets.mealPlanId],
      references: [mealPlan.id],
    }),
    slot: one(plannedMealSlot, {
      fields: [plannedMealNutrientTargets.mealSlotId],
      references: [plannedMealSlot.id],
    }),
  }),
);

export const plannedMeal = pgTable(
  "planned_meal",
  {
    ...id,
    ...temporalTimestamps,
    ...sortIndex,
    mealPlanId: uuid("meal_plan_id").notNull(),
    mealSlotId: uuid("meal_slot_id").notNull(),
    name: text("name").notNull(),
    userId: uuid("user_id").notNull(),
    groupId: uuid("group_id").notNull(),
  },
  (t) => [
    unique("planned_meal_unique").on(t.mealPlanId, t.id),
    unique("planned_meal_lane_unique").on(t.mealPlanId, t.id, t.mealSlotId, t.userId),
    uniqueIndex("planned_meal_lane_sort_index_unique").on(
      t.mealPlanId,
      t.mealSlotId,
      t.userId,
      t.sortIndex,
    ),
    foreignKey({
      name: "planned_meal_slot_fk",
      columns: [t.mealPlanId, t.mealSlotId],
      foreignColumns: [plannedMealSlot.mealPlanId, plannedMealSlot.id],
    }),
    foreignKey({
      name: "planned_meal_member_fk",
      columns: [t.mealPlanId, t.userId],
      foreignColumns: [mealPlanMember.mealPlanId, mealPlanMember.userId],
    }),
    uniqueIndex("planned_meal_group_lane_unique").on(
      t.mealPlanId,
      t.mealSlotId,
      t.userId,
      t.groupId,
    ),
  ],
);

export const plannedMealRelations = relations(plannedMeal, ({ one, many }) => ({
  mealPlan: one(mealPlan, {
    fields: [plannedMeal.mealPlanId],
    references: [mealPlan.id],
  }),
  slot: one(plannedMealSlot, {
    fields: [plannedMeal.mealSlotId],
    references: [plannedMealSlot.id],
  }),
  items: many(plannedMealItem),
  itemDescriptors: many(plannedMealItemDescriptor),
  occurrences: many(plannedMealOccurrence),
}));

export const plannedMealOccurrence = pgTable(
  "planned_meal_occurrence",
  {
    ...id,
    ...temporalTimestamps,
    date: temporalDate("date"),
    mealPlanId: uuid("meal_plan_id").notNull(),
    mealId: uuid("meal_id").notNull(),
    mealSlotId: uuid("meal_slot_id").notNull(),
    userId: uuid("user_id").notNull(),
  },
  (t) => [
    foreignKey({
      name: "planned_meal_occurrence_meal_lane_fk",
      columns: [t.mealPlanId, t.mealId, t.mealSlotId, t.userId],
      foreignColumns: [
        plannedMeal.mealPlanId,
        plannedMeal.id,
        plannedMeal.mealSlotId,
        plannedMeal.userId,
      ],
    }),
    uniqueIndex("planned_meal_occurrence_lane_date_unique")
      .on(t.mealPlanId, t.mealSlotId, t.userId, t.date)
      .where(sql`${t.date} is not null`),
  ],
);

export const plannedMealOccurrenceRelations = relations(plannedMealOccurrence, ({ one }) => ({
  meal: one(plannedMeal, {
    fields: [plannedMealOccurrence.mealId],
    references: [plannedMeal.id],
  }),
  foodLogSourceGroup: one(foodLogSourceGroup, {
    fields: [plannedMealOccurrence.id],
    references: [foodLogSourceGroup.sourceId],
  }),
}));

export const plannedMealItem = pgTable(
  "planned_meal_item",
  {
    ...id,
    ...temporalTimestamps,
    ...sortIndex,
    mealPlanId: uuid("meal_plan_id").notNull(),
    mealId: uuid("meal_id").notNull(),
    foodServingId: uuid("food_serving_id")
      .notNull()
      .references(() => foodServing.id),
    amount: doublePrecision("amount").notNull(),
  },
  (t) => [
    uniqueIndex("planned_meal_item_unique_sort_index").on(t.mealPlanId, t.mealId, t.sortIndex),
    uniqueIndex("planned_meal_item_unique_food_serving").on(t.mealId, t.foodServingId),
    foreignKey({
      name: "planned_meal_item_meal_fk",
      columns: [t.mealPlanId, t.mealId],
      foreignColumns: [plannedMeal.mealPlanId, plannedMeal.id],
    }),
  ],
);

export const plannedMealItemRelations = relations(plannedMealItem, ({ one }) => ({
  meal: one(plannedMeal, {
    fields: [plannedMealItem.mealId],
    references: [plannedMeal.id],
  }),
  serving: one(foodServing, {
    fields: [plannedMealItem.foodServingId],
    references: [foodServing.id],
  }),
}));

export const plannedMealItemDescriptor = pgTable(
  "planned_meal_item_descriptor",
  {
    ...id,
    ...temporalTimestamps,
    ...sortIndex,
    mealPlanId: uuid("meal_plan_id").notNull(),
    mealId: uuid("meal_id").notNull(),
    data: jsonb("data").$type<FoodItemMatcher.ItemDescriptor>().notNull(),
  },
  (t) => [
    uniqueIndex("planned_meal_item_descriptor_unique_sort_index").on(
      t.mealPlanId,
      t.mealId,
      t.sortIndex,
    ),
    foreignKey({
      name: "planned_meal_item_descriptor_meal_fk",
      columns: [t.mealPlanId, t.mealId],
      foreignColumns: [plannedMeal.mealPlanId, plannedMeal.id],
    }),
  ],
);

export const plannedMealItemDescriptorRelations = relations(
  plannedMealItemDescriptor,
  ({ one }) => ({
    meal: one(plannedMeal, {
      fields: [plannedMealItemDescriptor.mealId],
      references: [plannedMeal.id],
    }),
  }),
);
